import ApiClient, { getAccessToken } from '../../../lib/ApiClient'

const authService = {
  async login(email, password) {
    try {
      const response = await ApiClient.post('/auth/generate', {
        email,
        password,
        deviceInfo: this.generateDeviceInfo(),
      })
      // El servidor establece las cookies httpOnly automáticamente (para
      // entornos same-site) Y devuelve el accessToken en el cuerpo (para
      // entornos cross-site donde las cookies de terceros están bloqueadas).
      return response.data
    } catch (error) {
      throw new Error(this.handleAuthError(error))
    }
  },

  async logout() {
    try {
      await ApiClient.post('/auth/logout', {}, { timeout: 5000 })
    } catch (error) {
      console.warn('Error no crítico durante logout:', error.message)
    }
  },

  /**
   * Restaura la sesión en recargas de página.
   *
   * Estrategia dual:
   * 1. Si hay un accessToken en memoria (restaurado de sessionStorage en recarga),
   *    valida con GET /auth/verify enviando la cabecera Authorization.
   *    No lanza 401 — devuelve siempre 200 con { isValid: true|false }.
   * 2. Fallback: POST /auth/refresh (funciona en entornos same-site donde el
   *    navegador envía la cookie httpOnly; en cross-site devolverá 400).
   */
  async verifyToken() {
    const existingToken = getAccessToken()

    if (existingToken) {
      try {
        // El interceptor añadirá Authorization: Bearer <existingToken>
        const response = await ApiClient.get('/auth/verify')
        const data = response.data
        if (data?.isValid) {
          // Devolvemos el token actual para que AuthContext lo confirme en memoria
          return {
            isValid: true,
            userData: { ...data, accessToken: existingToken },
          }
        }
        // Token inválido/expirado → intentar refresh a continuación
      } catch {
        // Error inesperado → intentar refresh a continuación
      }
    }

    // Fallback: silent refresh con cookie httpOnly (same-site) o fallo limpio
    try {
      const response = await ApiClient.post('/auth/refresh')
      const data = response.data
      if (!data?.accessToken) return { isValid: false }
      return { isValid: true, userData: data }
    } catch {
      return { isValid: false }
    }
  },

  async refreshToken() {
    try {
      const response = await ApiClient.post('/auth/refresh')
      return response.data
    } catch (error) {
      throw new Error(this.handleAuthError(error))
    }
  },

  generateDeviceInfo() {
    return `WebApp/${navigator.platform} (${navigator.userAgent})`
  },

  handleAuthError(error) {
    if (!error.response) {
      return error.message?.includes('Network Error')
        ? 'No hay conexión con el servidor'
        : 'Error de red. Verifica tu conexión.'
    }

    const status = error.response.status
    const errorData = error.response.data || {}
    const apiMessage = errorData.message || errorData.error || ''

    const isNotVerified =
      apiMessage.toLowerCase().includes('no verificada') ||
      apiMessage.toLowerCase().includes('account_not_verified')

    if (isNotVerified) {
      return 'Cuenta no verificada. Revisa tu email para activar tu cuenta.'
    }

    const errorMap = {
      400: apiMessage || 'Solicitud incorrecta',
      401: 'Credenciales inválidas',
      403: 'Acceso no autorizado',
      429: 'Demasiados intentos. Por favor espera.',
      500: 'Error interno del servidor',
    }

    const specificMessages = {
      invalid_credentials: 'Email o contraseña incorrectos',
      account_not_verified: 'Cuenta no verificada',
      token_expired: 'Sesión expirada',
      token_revoked: 'Sesión finalizada en otro dispositivo',
    }

    return (
      specificMessages[apiMessage] ||
      errorMap[status] ||
      `Error ${status}: ${apiMessage}`
    )
  },
}

export default authService
